import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress, Button } from '@mui/material';
import config from '../config';
import { notifyRbacUpdated } from './rbac';

/** Same property-type normalisation the normal Login uses. */
const normalizePropertyType = (data) => {
  const v = data?.propertType ?? data?.PropertyType ?? data?.propertyType ?? '';
  return String(v).trim().toUpperCase();
};

/**
 * One-time direct-login landing page.
 * URL: /direct-login?t=<token>
 * The token is consumed server-side EXACTLY ONCE. On success we set the very
 * same sessionStorage keys the normal Login sets, then go to /admin. On any
 * failure (invalid / expired / already-used link) we bounce to /login.
 */
export default function DirectLogin() {
  const navigate = useNavigate();
  const ranRef = useRef(false); // StrictMode double-invoke guard — never POST twice.
  const [error, setError] = useState('');

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('t') || params.get('token') || '';

    if (!token) {
      setError('This login link is missing its token.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${config.API_BASE_URL}/account/direct-login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });

        if (!res.ok) {
          setError(
            'This login link is invalid, expired, or has already been used. Please log in normally.',
          );
          return;
        }

        const data = await res.json();
        if (data.message !== 'Login successful' || !data.user) {
          setError('This login link could not be used. Please log in normally.');
          return;
        }

        // ── Company / init state (mirrors Login.loadCompany) ──
        const companyDetails = data.companyDetails;
        if (companyDetails) {
          if (companyDetails.id != null) {
            sessionStorage.setItem('companyId', companyDetails.id);
          }
          const pt = normalizePropertyType(companyDetails);
          const normalized = pt
            ? { ...companyDetails, propertType: pt }
            : companyDetails;
          if (pt) sessionStorage.setItem('propertType', pt);
          sessionStorage.setItem('companyDetails', JSON.stringify(normalized));
        }
        if (data.client) {
          localStorage.setItem('client', data.client);
        }

        // ── Auth / permission state (mirrors Login handleLogin) ──
        sessionStorage.setItem('user', JSON.stringify(data.user));
        sessionStorage.setItem('username', data.user.username);
        sessionStorage.setItem('userId', data.user.id);
        sessionStorage.setItem('token', data.token);
        sessionStorage.setItem('permissions', JSON.stringify(data.permisioon));
        const rbacCodes = data.permissionCodes ?? data.PermissionCodes;
        if (Array.isArray(rbacCodes) && rbacCodes.length > 0) {
          sessionStorage.setItem('permissionCodes', JSON.stringify(rbacCodes));
        } else {
          sessionStorage.removeItem('permissionCodes');
        }
        notifyRbacUpdated();
        // The `pendingDashboardRefresh` flag is gone - it made the dashboard
        // reload the whole app once after signing in. See DashboardSwitcher.js.

        // Established user coming through a direct link — the setup gate is
        // already satisfied, so land straight on the dashboard.
        localStorage.setItem('hotelSetupDone', 'true');
        navigate('/admin', { replace: true });
      } catch (e) {
        console.error('❌ Direct-login error:', e);
        setError('Server error. Please log in normally.');
      }
    })();
  }, [navigate]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        px: 3,
        textAlign: 'center',
      }}
    >
      {!error ? (
        <>
          <CircularProgress />
          <Typography variant='body1'>Signing you in…</Typography>
        </>
      ) : (
        <>
          <Typography variant='h6' color='error'>
            Login link cannot be used
          </Typography>
          <Typography variant='body2' sx={{ maxWidth: 420 }}>
            {error}
          </Typography>
          <Button variant='contained' onClick={() => navigate('/login', { replace: true })}>
            Go to Login
          </Button>
        </>
      )}
    </Box>
  );
}
